import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useEffect } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Logo from '@/components/atoms/logo/logo';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import variables from '@/components/organisms/booking-flow-navigation/booking-flow-navigation.variables';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import Row from '@/components/utilities/row/row';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { getLogoName, LogoType } from '@/libraries/getters/get-logo-name';
import styles from './booking-flow-loader.module.scss';

export interface BookingFlowLoaderProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the close event. */
  onClose: (value?: boolean) => void;
}

const BookingFlowLoader = ({
  className,
  isOpen = false,
  onClose,
}: BookingFlowLoaderProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();

  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handler, true);

    return () => document.removeEventListener('keydown', handler, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={styles.content}>
        <Container>
          <Row>
            <Column offset={{ laptop: 1 }} width={{ laptop: 10 }}>
              <div className={styles.navigation}>
                <div className={styles.logoContainer}>
                  <Logo
                    className={styles.logo}
                    name={getLogoName({
                      brand,
                      type: variables.logo as LogoType,
                    })}
                  />
                </div>

                <div className={styles.bookingContainer}>
                  <Typography tagStyle="bodyMediumBold">
                    {t('items.modules.bookingFlow.booking')}
                  </Typography>

                  <div className={styles.timerContainer}>
                    <Icon className={styles.timerIcon} name="Clock" size={14} />

                    <Typography
                      className={styles.countdownContainer}
                      tagStyle="bodySmall"
                    >
                      {`${t('items.modules.bookingFlow.timeLeft')}: `}

                      <div className={styles.countdown}>{'00:00'}</div>
                    </Typography>
                  </div>
                </div>
              </div>

              <Spinner
                className={styles.spinner}
                hasTransparentOverlay
                loadingText={`${t('items.modules.bookingFlow.loading')}...`}
              />
            </Column>
          </Row>
        </Container>
      </div>
    </Lightbox>
  );
};

BookingFlowLoader.displayName = 'BookingFlowLoader';

export default BookingFlowLoader;
